import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AdminSettingsPage = () => {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.patch('/settings', settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl space-y-8">
        <div>
          <h1 className="text-2xl font-syne font-bold text-white">Settings</h1>
          <p className="text-fp-textMuted text-sm mt-1">Configure your agency settings</p>
        </div>

        {/* Agency Info */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <h2 className="font-syne font-bold text-white mb-4">Agency Information</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-fp-textMuted">Agency Name</Label>
              <Input
                value={settings.agency_name || ''}
                onChange={(e) => setSettings({ ...settings, agency_name: e.target.value })}
                className="mt-1 bg-fp-bg border-white/10 text-white"
                data-testid="input-agency-name"
              />
            </div>
            <div>
              <Label className="text-fp-textMuted">Logo URL</Label>
              <Input
                value={settings.agency_logo || ''}
                onChange={(e) => setSettings({ ...settings, agency_logo: e.target.value })}
                className="mt-1 bg-fp-bg border-white/10 text-white"
                data-testid="input-agency-logo"
              />
            </div>
            <div>
              <Label className="text-fp-textMuted">Admin Email</Label>
              <Input
                type="email"
                value={settings.admin_email || ''}
                onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                className="mt-1 bg-fp-bg border-white/10 text-white"
                data-testid="input-admin-email"
              />
            </div>
          </div>
        </div>

        {/* Contact Settings */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <h2 className="font-syne font-bold text-white mb-4">Contact Settings</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-fp-textMuted">WhatsApp Number</Label>
              <Input
                value={settings.whatsapp_number || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                placeholder="+91XXXXXXXXXX"
                className="mt-1 bg-fp-bg border-white/10 text-white"
                data-testid="input-whatsapp"
              />
            </div>
            <div>
              <Label className="text-fp-textMuted">Calendly Link</Label>
              <Input
                value={settings.calendly_link || ''}
                onChange={(e) => setSettings({ ...settings, calendly_link: e.target.value })}
                placeholder="https://calendly.com/..."
                className="mt-1 bg-fp-bg border-white/10 text-white"
                data-testid="input-calendly"
              />
            </div>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="bg-fp-surface border border-white/5 rounded-sm p-6">
          <h2 className="font-syne font-bold text-white mb-4">Theme Settings</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-fp-textMuted">Accent Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <Input
                  type="color"
                  value={settings.accent_color || '#D4A01D'}
                  onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                  className="w-12 h-12 p-1 bg-fp-bg border-white/10"
                />
                <Input
                  value={settings.accent_color || '#D4A01D'}
                  onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                  className="flex-1 bg-fp-bg border-white/10 text-white"
                  data-testid="input-accent-color"
                />
              </div>
            </div>
            <div>
              <Label className="text-fp-textMuted">Background Mode</Label>
              <Select
                value={settings.background_mode || 'dark'}
                onValueChange={(value) => setSettings({ ...settings, background_mode: value })}
              >
                <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white" data-testid="select-bg-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-fp-surface border-white/10">
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Film Grain Overlay</Label>
                <p className="text-fp-textMuted text-sm">Add subtle grain texture to public pages</p>
              </div>
              <Switch
                checked={settings.enable_grain || false}
                onCheckedChange={(checked) => setSettings({ ...settings, enable_grain: checked })}
                data-testid="toggle-grain"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={saveSettings}
          className="btn-primary w-full"
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
